import { Row, Col, Divider, Collapse, Tabs, Card, Table, Radio, Tag } from 'antd';
import Data from '../../CustomText/data';
import { formatDate, convertCentsIntoDollar } from '../../../helpers/genericHelper';
import API from '../../../services/API';
import { useState, useEffect } from 'react';
import { PersistedCollapse, PersistedTabs } from '../../PersistedSections';
import usePageState from '../../../helpers/usePageState';

const { Panel } = Collapse;
const { TabPane } = Tabs;

const getAvaStatusColor = (status) => {
	switch (status) {
		case 'DASHBOARD':
		case 'VERIFIED':
			return 'green';
		case 'DELETED':
		case 'WILL_DELETE':
			return 'red';
		default:
			return 'default';
	}
};

const getBloomStatusColor = (status) => {
	switch (status) {
		case 'VERIFIED':
			return 'green';
		case 'PENDING':
			return 'orange';
		case 'UNKNOWN':
		default:
			return 'red';
	}
};

export default function BloomData({ bloomData, userId }) {
	const [tradelinesData, setTradelinesData] = useState(null);
	const [connectionsData, setConnectionsData] = useState(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				let conResp = await API.bloomConnections(userId);
				setConnectionsData(conResp.connections);

				let tradeResp = await API.tradelines(userId, false);
				setTradelinesData(tradeResp.tradelines);
			} catch (error) {
				console.error('Error fetching bloom data:', error);
			}
		};

		fetchData();
	}, [userId]);

	const isEnrolled = !tradelinesData ? null : (bloomData?.avaStatus && bloomData.avaStatus !== 'DELETED' && bloomData.avaStatus !== 'WILL_DELETE' && tradelinesData.some(t => t.attestation));

	return (<>
		<Row justify="center" gutter={[16, 16]}>
			<Col span={4}>
				<Data title="Enrolled (any attested Tradeline)" value={<Tag color={isEnrolled ? 'green' : 'red'}>{isEnrolled ? 'Yes' : 'No'}</Tag>} />

			</Col>
			<Col span={2}>
				<Data title="Ava Status" value={bloomData?.avaStatus ? <Tag color={getAvaStatusColor(bloomData.avaStatus)}>{bloomData.avaStatus}</Tag> : null} />
			</Col>
			<Col span={2}>
				<Data title="Bloom Status" value={bloomData?.bloomStatus ? <Tag color={getBloomStatusColor(bloomData.bloomStatus)}>{bloomData.bloomStatus}</Tag> : null} />
			</Col>

			<Col span={5}>
				<Data title="Bloom User ID" value={bloomData?.bloomUserId} copyable />
			</Col>
			<Col span={3}>
				<Data title="Entry Source" value={bloomData?.entrySource} />
			</Col>
			<Col span={6}>
				<Data title="Deleted Reason" value={bloomData?.deletedReason} />
			</Col>
		</Row>
		<Divider orientation="center">Timestamps</Divider>
		<Row justify="center" gutter={[16, 16]}>
			<Col span={4}>
				<Data title="Created" value={formatDate(bloomData?.createTs, true)} />
			</Col>
			<Col span={4}>
				<Data title="Entry" value={bloomData?.entryTs ? formatDate(bloomData.entryTs, true) : null} />
			</Col>
			<Col span={4}>
				<Data title="First Prescreen Trigger" value={bloomData?.firstPrescreenTriggerTs ? formatDate(bloomData.firstPrescreenTriggerTs, true) : null} />
			</Col>
			<Col span={4}>
				<Data title="Last Prescreen Trigger" value={bloomData?.lastPrescreenTriggerTs ? formatDate(bloomData.lastPrescreenTriggerTs, true) : null} />
			</Col>
			<Col span={4}>
				<Data title="Last Activity" value={bloomData?.lastActivityDate ? formatDate(bloomData.lastActivityDate, true) : null} />
			</Col>
			<Col span={4}>
				<Data title="Bloom User Updated" value={bloomData?.bloomUserUpdatedAt ? formatDate(bloomData.bloomUserUpdatedAt, true) : null} />
			</Col>
			<Col span={4}>
				<Data title="Updated" value={bloomData?.updateTs ? formatDate(bloomData.updateTs, true) : null} />
			</Col>
			<Col span={4}>
				<Data title="Deleted" value={bloomData?.deletedTs ? formatDate(bloomData.deletedTs, true) : null} />
			</Col>
		</Row>
		<Divider />
		<ConnectionsComponent connectionsData={connectionsData} />
		<TradelinesComponent tradelinesData={tradelinesData} />
	</>);
}

const ConnectionsComponent = ({ connectionsData }) => {
	if (!connectionsData || connectionsData.length === 0) {
		return <Data title="Connections" value="No connections found" />;
	}

	return (
		<div style={{ marginBottom: '20px' }}>
			<h2>Bank Connections</h2>
			{connectionsData.map((connection) => (
				<Card key={connection.id} style={{ marginBottom: '16px' }}>
					<h4>{connection.institutionName}</h4>
					<Row gutter={[16, 16]}>
						<Col span={5}>
							<Data title="Bloom Connection ID" value={connection.bloomConnectionId} copyable />
						</Col>
						<Col span={4}>
							<Data title="Connection Status" value={connection.connectionStatus} />
						</Col>
						<Col span={4}>
							<Data title="Status Changed" value={formatDate(connection.statusChangedTs, true)} />
						</Col>
						<Col span={4}>
							<Data title="Last Error" value={connection.lastErrorTs ? formatDate(connection.lastErrorTs, true) : 'N/A'} />
						</Col>
						<Col span={4}>
							<Data title="Error Resolved" value={connection.errorResolvedTs ? formatDate(connection.errorResolvedTs, true) : 'N/A'} />
						</Col>
						<Col span={4}>
							<Data title="Last Sync" value={connection.lastWebhookTs ? formatDate(connection.lastWebhookTs, true) : 'N/A'} />
						</Col>
						<Col span={5}>
							<Data title="Plaid Account ID" value={connection.plaidAccountId || 'N/A'} />
						</Col>
						<Col span={4}>
							<Data title="Created" value={formatDate(connection.createTs, true)} />
						</Col>
						<Col span={4}>
							<Data title="Updated" value={formatDate(connection.updateTs, true)} />
						</Col>
					</Row>
				</Card>
			))}
		</div>
	);
};

const TradelinesComponent = ({ tradelinesData }) => {
	const [filterStatus, setFilterStatus] = usePageState('bloom-tradeline-filter', 'OPEN');

	if (!tradelinesData || tradelinesData.length === 0) {
		return <Data title="Tradelines" value="No tradelines found" />;
	}

	const openedCount = tradelinesData.filter(t => t.status === 'OPEN').length;
	const unopenedCount = tradelinesData.filter(t => t.status === 'UNOPENED').length;
	const closedCount = tradelinesData.filter(t => t.status === 'CLOSED').length;

	const filteredTradelines = tradelinesData.filter(t => t.status === filterStatus)
	return (<>
		<h2>Tradelines</h2>
		<div style={{ marginBottom: '16px' }}>
			<Radio.Group value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} buttonStyle='solid'>
				<Radio.Button value="OPEN">Opened ({openedCount})</Radio.Button>
				<Radio.Button value="UNOPENED">Unopened ({unopenedCount})</Radio.Button>
				<Radio.Button value="CLOSED">Closed ({closedCount})</Radio.Button>
			</Radio.Group>
		</div>
		<PersistedTabs name={`bloom-tradeline-tab:${filterStatus}`} defaultActiveKey="0">
			{filteredTradelines.map((tradeline, index) => (
				<TabPane tab={`${tradeline.merchant}`} key={index}>
					<Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
						<Col span={2}>
							<Data title="Merchant" value={tradeline.merchant} />
						</Col>
						<Col span={2}>
							<Data title="Category" value={tradeline.category} />
						</Col>
						<Col span={2}>
							<Data title="Status" value={tradeline.status} />
						</Col>
						<Col span={2}>
							<Data title="Is Eligible" value={tradeline.isEligible ? 'Yes' : 'No'} />
						</Col>
						<Col span={2}>
							<Data title="Is Ava Eligible" value={tradeline.isAvaEligible ? 'Yes' : 'No'} />
						</Col>
						<Col span={2}>
							<Data title="Is Ava Processed" value={tradeline.isAvaProcessed ? 'Yes' : 'No'} />
						</Col>
						<Col span={2}>
							<Data title="Active" value={tradeline.active ? 'Yes' : 'No'} />
						</Col>
						<Col span={2}>
							<Data title="Closed By User" value={tradeline.closedByUser ? 'Yes' : 'No'} />
						</Col>
						<Col span={2}>
							<Data title="Payment Frequency" value={tradeline.paymentFrequency} />
						</Col>
						<Col span={3}>
							<Data title="Last Transaction Date" value={formatDate(tradeline.lastTransactionDate, true)} />
						</Col>
						<Col span={3}>
							<Data title="Open Date" value={tradeline.openDate ? formatDate(tradeline.openDate, true) : 'N/A'} />
						</Col>
						<Col span={5}>
							<Data title="Bloom Tradeline ID" value={tradeline.bloomTradelineId} copyable />
						</Col>
						<Col span={4}>
							<Data title="Created" value={formatDate(tradeline.createTs, true)} />
						</Col>
						<Col span={4}>
							<Data title="Updated" value={formatDate(tradeline.updateTs, true)} />
						</Col>
						<Col span={5}>
							<Data title="Bloom User ID" value={tradeline.bloomUserId} copyable />
						</Col>
						<Col span={5}>
							<Data title="Bloom Created At" value={formatDate(tradeline.bloomCreatedAt, true)} />
						</Col>
						<Col span={5}>
							<Data title="Bloom Updated At" value={formatDate(tradeline.bloomUpdatedAt, true)} />
						</Col>
					</Row>
					<Divider />
					{tradeline.attestation && (
						<div style={{ marginBottom: '36px' }}>
							<h3>Attestation Details</h3>
							<Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
								<Col span={5}>
									<Data title="Attestation ID" value={tradeline.attestation.attestationId} />
								</Col>
								<Col span={5}>
									<Data title="Session ID" value={tradeline.attestation.sessionId} />
								</Col>
								<Col span={5}>
									<Data title="Tradeline ID" value={tradeline.attestation.tradelineId} />
								</Col>
								<Col span={2}>
									<Data title="Status" value={tradeline.attestation.status} />
								</Col>
								<Col span={2}>
									<Data title="Category" value={tradeline.attestation.category} />
								</Col>
								<Col span={4}>
									{tradeline.attestation.details.leaseStartDate && (
										<Data title="Lease Start Date" value={formatDate(tradeline.attestation.details.leaseStartDate)} />
									)}
								</Col>
								<Col span={4}>
									<Data title="Created" value={formatDate(tradeline.attestation.createTs, true)} />
								</Col>
								<Col span={4}>
									<Data title="Updated" value={formatDate(tradeline.attestation.updateTs, true)} />
								</Col>
							</Row>
							{tradeline.attestation.details && (
								<Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
									<Col span={2}>
										{tradeline.attestation.details.isPrimaryAddress !== undefined && (
											<Data title="Is Primary Address" value={tradeline.attestation.details.isPrimaryAddress ? 'Yes' : 'No'} />
										)}
									</Col>
									<Col span={3}>
										{tradeline.attestation.details.hasOtherMortgageOrRent !== undefined && (
											<Data title="Has Other Mortgage or Rent" value={tradeline.attestation.details.hasOtherMortgageOrRent ? 'Yes' : 'No'} />
										)}
									</Col>
									<Col span={4}>
										{tradeline.attestation.details.servicePhone && (
											<Data title="Service Phone" value={tradeline.attestation.details.servicePhone} />
										)}
									</Col>
									<Col span={5}>
										{tradeline.attestation.details.utilityTypes && (
											<Data title="Utility Types" value={tradeline.attestation.details.utilityTypes.join(', ')} />
										)}
									</Col>
								</Row>
							)}
						</div>
					)}
					<PersistedCollapse name={`bloom-transactions:${tradeline.bloomTradelineId}`}>
						<Panel header={`Transactions (${tradeline.transactions.length})`} key="transactions">
							<Table dataSource={tradeline.transactions}
								rowKey="id"
								pagination={false}
								size="small"
							>
								<Table.Column title="Transaction Date"
									dataIndex="transactionDate"
									render={(date) => formatDate(date)}
								/>
								<Table.Column title="Amount"
									dataIndex="amountCents"
									render={(cents) => convertCentsIntoDollar(cents)}
								/>
								<Table.Column title="Currency" dataIndex="currency"
								/>
								<Table.Column title="Bloom Transaction ID"
									dataIndex="bloomTransactionId"
									render={(id) => <Data value={id} />}
								/>
								<Table.Column title="Bloom Tradeline ID"
									dataIndex="bloomTradelineId"
									render={(id) => <Data value={id} />}
								/>
								<Table.Column title="Created"
									dataIndex="createTs"
									render={(date) => formatDate(date, true)}
								/>
								<Table.Column title="Updated"
									dataIndex="updateTs"
									render={(date) => formatDate(date, true)}
								/>
							</Table>
						</Panel>
					</PersistedCollapse>
				</TabPane>
			))}
		</PersistedTabs>
	</>);
};

